import { useEffect, useState, Fragment } from "react";
import api from "../services/api";
import { IndianRupee, TrendingUp, Package, Loader2, ArrowUpFromLine, Clock, Check, X, Wallet, Landmark, Smartphone, ChevronDown, ChevronUp, Percent, Edit3, Save, Sparkles, Truck, Zap, GraduationCap, Flame } from "lucide-react";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";

export default function Earnings() {
  const [data, setData] = useState(null);
  const [payouts, setPayouts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [requesting, setRequesting] = useState(false);
  const [tab, setTab] = useState("earnings");
  const [withdrawOpen, setWithdrawOpen] = useState(false);
  const [withdrawMethod, setWithdrawMethod] = useState("upi");
  const [bankDetails, setBankDetails] = useState({ upi_id: "", bank_account: "", bank_ifsc: "", bank_name: "", account_holder: "" });
  const [editingBank, setEditingBank] = useState(false);
  const [savingBank, setSavingBank] = useState(false);

  const fetchData = async () => {
    try {
      const [eRes, pRes, bRes] = await Promise.all([
        api.get("/earnings/my"),
        api.get("/payouts/my"),
        api.get("/agent/bank-details").catch(() => ({ data: {} })),
      ]);
      setData(eRes.data);
      setPayouts(pRes.data);
      if (bRes.data) setBankDetails(prev => ({ ...prev, ...bRes.data }));
    } catch (err) {
      console.error("Failed to load earnings:", err.response?.data?.detail || err.message);
    }
    setLoading(false);
  };

  useEffect(() => { fetchData(); }, []);

  const saveBankDetails = async () => {
    setSavingBank(true);
    try {
      await api.put("/agent/bank-details", bankDetails);
      toast.success("Bank details saved!");
      setEditingBank(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save");
    }
    setSavingBank(false);
  };

  const requestPayout = async () => {
    if (withdrawMethod === "upi" && !bankDetails.upi_id) {
      toast.error("Please enter your UPI ID first");
      setEditingBank(true);
      return;
    }
    if (withdrawMethod === "bank_transfer" && !bankDetails.bank_account) {
      toast.error("Please enter your bank details first");
      setEditingBank(true);
      return;
    }
    setRequesting(true);
    try {
      const payload = {
        withdrawal_method: withdrawMethod,
        upi_id: bankDetails.upi_id,
        bank_account: bankDetails.bank_account,
        bank_ifsc: bankDetails.bank_ifsc,
        bank_name: bankDetails.bank_name,
        account_holder: bankDetails.account_holder,
      };
      const { data: resp } = await api.post("/payouts/request", payload);
      if (resp.payout?.status === "failed") {
        toast.error(`Payout failed: ${resp.payout?.error || "Unknown error"}`);
      } else {
        toast.success(`Withdrawal of INR ${resp.payout?.amount} requested via ${withdrawMethod === "upi" ? "UPI" : "Bank Transfer"}! Admin will process within 10-20 minutes.`, { duration: 6000 });
      }
      setWithdrawOpen(false);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to request payout");
    }
    setRequesting(false);
  };

  const statusBadge = (status) => {
    const map = {
      pending: { color: "bg-amber-50 text-amber-700 border-amber-200", icon: Clock, label: "Pending" },
      processing: { color: "bg-blue-50 text-blue-700 border-blue-200", icon: Loader2, label: "Processing" },
      approved: { color: "bg-emerald-50 text-emerald-700 border-emerald-200", icon: Check, label: "Paid" },
      paid: { color: "bg-emerald-50 text-emerald-700 border-emerald-200", icon: Check, label: "Paid" },
      rejected: { color: "bg-red-50 text-red-700 border-red-200", icon: X, label: "Rejected" },
      failed: { color: "bg-red-50 text-red-700 border-red-200", icon: X, label: "Failed" },
    };
    const cfg = map[status] || map.pending;
    const Icon = cfg.icon;
    return (
      <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-lg border text-[11px] font-semibold ${cfg.color}`}>
        <Icon className={`h-3 w-3 ${status === "processing" ? "animate-spin" : ""}`} /> {cfg.label}
      </span>
    );
  };

  const methodBadge = (method) => {
    if (method === "upi") return <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[10px] font-bold bg-purple-50 text-purple-700 border border-purple-200"><Smartphone className="h-3 w-3" /> UPI</span>;
    return <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[10px] font-bold bg-blue-50 text-blue-700 border border-blue-200"><Landmark className="h-3 w-3" /> Bank</span>;
  };

  const [expandedRow, setExpandedRow] = useState(null);

  const breakdownChips = (e) => {
    const bd = e.gig_breakdown || {};
    if (e.commission_model === "legacy_80pct") {
      return [
        { label: "Legacy 80% of order", value: e.order_total ?? 0, color: "bg-slate-50 text-slate-700 border-slate-200", icon: Percent },
        { label: "Packing deducted", value: e.packing_deducted ?? 0, color: "bg-amber-50 text-amber-700 border-amber-200", icon: Package, hideIfZero: true },
      ];
    }
    return [
      { label: "Base Pickup", value: bd.base_pickup_fee ?? 0, color: "bg-blue-50 text-blue-700 border-blue-200", icon: Package },
      { label: `Distance (${bd.distance_km ?? 0} km)`, value: bd.distance_bonus ?? 0, color: "bg-emerald-50 text-emerald-700 border-emerald-200", icon: Truck, hideIfZero: true },
      { label: "Surge", value: bd.surge_bonus ?? 0, color: "bg-orange-50 text-orange-700 border-orange-200", icon: Zap, hideIfZero: true },
      { label: "College", value: bd.college_bonus ?? 0, color: "bg-violet-50 text-violet-700 border-violet-200", icon: GraduationCap, hideIfZero: true },
      { label: "Density Bonus", value: bd.density_bonus ?? 0, color: "bg-pink-50 text-pink-700 border-pink-200", icon: Flame, hideIfZero: true },
    ];
  };

  if (loading) return (
    <div className="flex items-center justify-center py-20">
      <Loader2 className="h-8 w-8 animate-spin text-[#FF6B00]" />
    </div>
  );

  return (
    <div className="space-y-6" data-testid="earnings-page">
      <div className="animate-fadeInUp">
        <h1 className="font-['Outfit'] text-2xl sm:text-3xl font-bold text-slate-900">My Earnings</h1>
        <p className="text-sm text-slate-500 mt-1">You earn for every delivery. Withdraw via UPI or Bank Transfer.</p>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5">
        <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-5 card-hover animate-fadeInUp" data-testid="total-earnings-card">
          <div className="flex items-start justify-between mb-3">
            <div className="w-10 h-10 bg-green-50 rounded-xl flex items-center justify-center">
              <IndianRupee className="h-5 w-5 text-green-600" />
            </div>
            <span className="text-[10px] font-bold text-green-600 uppercase tracking-wider flex items-center gap-1">
              <span className="w-1.5 h-1.5 rounded-full bg-green-500 live-dot" /> Live
            </span>
          </div>
          <p className="font-['Outfit'] text-2xl font-bold text-slate-900 tracking-tight">
            {data?.currency} {data?.total ?? 0}
          </p>
          <p className="text-xs text-slate-500 mt-1">Total Earnings</p>
        </div>

        <div className="bg-white rounded-2xl shadow-sm border border-emerald-100 p-5 card-hover animate-fadeInUp stagger-1" data-testid="available-balance-card">
          <div className="w-10 h-10 bg-emerald-50 rounded-xl flex items-center justify-center mb-3">
            <Wallet className="h-5 w-5 text-emerald-600" />
          </div>
          <p className="font-['Outfit'] text-2xl font-bold text-emerald-700 tracking-tight">
            {data?.currency} {data?.available_balance ?? 0}
          </p>
          <p className="text-xs text-slate-500 mt-1">Available to Withdraw</p>
        </div>

        <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-5 card-hover animate-fadeInUp stagger-2" data-testid="deliveries-count-card">
          <div className="w-10 h-10 bg-orange-50 rounded-xl flex items-center justify-center mb-3">
            <Package className="h-5 w-5 text-[#FF6B00]" />
          </div>
          <p className="font-['Outfit'] text-2xl font-bold text-slate-900 tracking-tight">{data?.deliveries ?? 0}</p>
          <p className="text-xs text-slate-500 mt-1">Total Deliveries</p>
        </div>

        <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-5 card-hover animate-fadeInUp stagger-3" data-testid="avg-earnings-card">
          <div className="w-10 h-10 bg-blue-50 rounded-xl flex items-center justify-center mb-3">
            <TrendingUp className="h-5 w-5 text-blue-600" />
          </div>
          <p className="font-['Outfit'] text-2xl font-bold text-slate-900 tracking-tight">
            {data?.currency} {data?.deliveries > 0 ? Math.round(data.total / data.deliveries) : 0}
          </p>
          <p className="text-xs text-slate-500 mt-1">Avg per Delivery</p>
        </div>
      </div>

      {/* Withdraw Section */}
      {(data?.available_balance || 0) >= 10 && (
        <div className="bg-gradient-to-r from-emerald-50 to-green-50 rounded-2xl border border-emerald-200/60 overflow-hidden animate-fadeInUp" data-testid="withdraw-section">
          <div className="p-5 flex items-center justify-between">
            <div>
              <p className="text-sm font-semibold text-emerald-800">Ready to withdraw?</p>
              <p className="text-xs text-emerald-600 mt-0.5">Available: INR {data?.available_balance} — Choose UPI or Bank Transfer</p>
            </div>
            <Button
              onClick={() => setWithdrawOpen(!withdrawOpen)}
              className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl gap-2 shadow-sm"
              data-testid="withdraw-toggle-btn"
            >
              <ArrowUpFromLine className="h-4 w-4" />
              Withdraw
              {withdrawOpen ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
            </Button>
          </div>

          {withdrawOpen && (
            <div className="border-t border-emerald-200/60 px-5 py-5 space-y-4 bg-white/50" data-testid="withdraw-form">
              {/* Method Toggle */}
              <div>
                <Label className="text-xs font-semibold text-slate-600 uppercase tracking-wider mb-2 block">Withdrawal Method</Label>
                <div className="grid grid-cols-2 gap-3">
                  <button
                    onClick={() => setWithdrawMethod("upi")}
                    className={`flex items-center gap-3 p-3.5 rounded-xl border-2 transition-all ${withdrawMethod === "upi" ? "border-purple-400 bg-purple-50/50 ring-1 ring-purple-200" : "border-slate-200 hover:border-slate-300"}`}
                    data-testid="withdraw-method-upi"
                  >
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${withdrawMethod === "upi" ? "bg-purple-500" : "bg-slate-100"}`}>
                      <Smartphone className={`h-5 w-5 ${withdrawMethod === "upi" ? "text-white" : "text-slate-500"}`} />
                    </div>
                    <div className="text-left">
                      <p className={`text-sm font-semibold ${withdrawMethod === "upi" ? "text-purple-700" : "text-slate-700"}`}>UPI Transfer</p>
                      <p className="text-[10px] text-slate-400">Instant to your UPI ID</p>
                    </div>
                  </button>
                  <button
                    onClick={() => setWithdrawMethod("bank_transfer")}
                    className={`flex items-center gap-3 p-3.5 rounded-xl border-2 transition-all ${withdrawMethod === "bank_transfer" ? "border-blue-400 bg-blue-50/50 ring-1 ring-blue-200" : "border-slate-200 hover:border-slate-300"}`}
                    data-testid="withdraw-method-bank"
                  >
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${withdrawMethod === "bank_transfer" ? "bg-blue-500" : "bg-slate-100"}`}>
                      <Landmark className={`h-5 w-5 ${withdrawMethod === "bank_transfer" ? "text-white" : "text-slate-500"}`} />
                    </div>
                    <div className="text-left">
                      <p className={`text-sm font-semibold ${withdrawMethod === "bank_transfer" ? "text-blue-700" : "text-slate-700"}`}>Bank Transfer</p>
                      <p className="text-[10px] text-slate-400">NEFT/IMPS to bank account</p>
                    </div>
                  </button>
                </div>
              </div>

              {/* UPI Details */}
              {withdrawMethod === "upi" && (
                <div className="space-y-3">
                  <div>
                    <Label className="text-xs font-medium text-slate-600">UPI ID</Label>
                    <Input
                      placeholder="yourname@upi"
                      value={bankDetails.upi_id}
                      onChange={(e) => setBankDetails({ ...bankDetails, upi_id: e.target.value })}
                      className="rounded-xl border-slate-200 h-11 mt-1 focus:border-purple-400 focus:ring-purple-200"
                      data-testid="upi-id-input"
                    />
                  </div>
                </div>
              )}

              {/* Bank Details */}
              {withdrawMethod === "bank_transfer" && (
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs font-medium text-slate-600">Account Holder Name</Label>
                    <Input
                      placeholder="Full Name"
                      value={bankDetails.account_holder}
                      onChange={(e) => setBankDetails({ ...bankDetails, account_holder: e.target.value })}
                      className="rounded-xl border-slate-200 h-11 mt-1"
                      data-testid="bank-holder-input"
                    />
                  </div>
                  <div>
                    <Label className="text-xs font-medium text-slate-600">Bank Name</Label>
                    <Input
                      placeholder="e.g., SBI, HDFC"
                      value={bankDetails.bank_name}
                      onChange={(e) => setBankDetails({ ...bankDetails, bank_name: e.target.value })}
                      className="rounded-xl border-slate-200 h-11 mt-1"
                      data-testid="bank-name-input"
                    />
                  </div>
                  <div>
                    <Label className="text-xs font-medium text-slate-600">Account Number</Label>
                    <Input
                      placeholder="XXXXXXXXXXXX"
                      value={bankDetails.bank_account}
                      onChange={(e) => setBankDetails({ ...bankDetails, bank_account: e.target.value })}
                      className="rounded-xl border-slate-200 h-11 mt-1"
                      data-testid="bank-account-input"
                    />
                  </div>
                  <div>
                    <Label className="text-xs font-medium text-slate-600">IFSC Code</Label>
                    <Input
                      placeholder="e.g., SBIN0001234"
                      value={bankDetails.bank_ifsc}
                      onChange={(e) => setBankDetails({ ...bankDetails, bank_ifsc: e.target.value })}
                      className="rounded-xl border-slate-200 h-11 mt-1"
                      data-testid="bank-ifsc-input"
                    />
                  </div>
                </div>
              )}

              {/* Withdraw Button */}
              <div className="flex items-center gap-3 pt-1">
                <Button
                  onClick={requestPayout}
                  disabled={requesting}
                  className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl gap-2 shadow-sm flex-1 h-11"
                  data-testid="confirm-withdraw-btn"
                >
                  {requesting ? <Loader2 className="h-4 w-4 animate-spin" /> : <ArrowUpFromLine className="h-4 w-4" />}
                  {requesting ? "Processing..." : `Withdraw INR ${data?.available_balance} via ${withdrawMethod === "upi" ? "UPI" : "Bank Transfer"}`}
                </Button>
                <Button
                  variant="outline"
                  onClick={saveBankDetails}
                  disabled={savingBank}
                  className="rounded-xl h-11 gap-1.5 text-slate-600"
                  data-testid="save-bank-btn"
                >
                  {savingBank ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                  Save Details
                </Button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Tabs */}
      <div className="flex items-center gap-3">
        <button onClick={() => setTab("earnings")} className={`px-4 py-2 rounded-xl text-sm font-semibold transition-all ${tab === "earnings" ? "bg-[#FF6B00] text-white shadow-sm" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`} data-testid="tab-earnings">
          Earnings History
        </button>
        <button onClick={() => setTab("payouts")} className={`px-4 py-2 rounded-xl text-sm font-semibold transition-all ${tab === "payouts" ? "bg-[#FF6B00] text-white shadow-sm" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`} data-testid="tab-payouts">
          Payout History ({payouts.length})
        </button>
      </div>

      {/* Earnings History */}
      {tab === "earnings" && (
        <div className="bg-white rounded-2xl shadow-sm border border-slate-100 animate-fadeInUp" data-testid="earnings-history">
          <div className="p-5 border-b border-slate-100">
            <h2 className="font-['Outfit'] text-lg font-semibold text-slate-900">Earnings History</h2>
            <p className="text-xs text-slate-400 mt-0.5">Earnings from completed deliveries</p>
          </div>
          {!data?.history?.length ? (
            <div className="p-12 text-center">
              <div className="w-14 h-14 bg-slate-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <IndianRupee className="h-7 w-7 text-slate-300" />
              </div>
              <p className="text-base font-semibold text-slate-700">No earnings yet</p>
              <p className="text-sm text-slate-400 mt-1">Complete deliveries to start earning!</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="border-slate-100 bg-slate-50/60">
                    <TableHead className="text-[10px] font-bold uppercase tracking-widest text-slate-400 pl-6 py-3 w-8"></TableHead>
                    <TableHead className="text-[10px] font-bold uppercase tracking-widest text-slate-400 py-3">Order</TableHead>
                    <TableHead className="text-[10px] font-bold uppercase tracking-widest text-slate-400 py-3">Order Total</TableHead>
                    <TableHead className="text-[10px] font-bold uppercase tracking-widest text-slate-400 py-3">You Earned</TableHead>
                    <TableHead className="text-[10px] font-bold uppercase tracking-widest text-slate-400 py-3">Date</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data.history.map((e, i) => {
                    const rowKey = e.tracking_id || i;
                    const expanded = expandedRow === rowKey;
                    const chips = breakdownChips(e).filter((c) => !(c.hideIfZero && !c.value));
                    const hasBreakdown = !!e.commission_model;
                    return (
                      <Fragment key={rowKey}>
                        <TableRow
                          className={`border-slate-50 row-hover ${hasBreakdown ? "cursor-pointer" : ""}`}
                          onClick={() => hasBreakdown && setExpandedRow(expanded ? null : rowKey)}
                          data-testid={`earning-row-${e.tracking_id}`}
                        >
                          <TableCell className="pl-6 py-3 w-8">
                            {hasBreakdown ? (
                              expanded
                                ? <ChevronUp className="h-3.5 w-3.5 text-slate-400" />
                                : <ChevronDown className="h-3.5 w-3.5 text-slate-400" />
                            ) : null}
                          </TableCell>
                          <TableCell className="py-3">
                            <div className="flex items-center gap-2">
                              <span className="text-sm font-mono font-semibold text-slate-900">{e.tracking_id}</span>
                              {e.commission_model === "gig" && (
                                <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[9px] font-bold bg-emerald-50 text-emerald-700 border border-emerald-200">
                                  <Sparkles className="h-2.5 w-2.5" /> GIG
                                </span>
                              )}
                              {e.commission_model === "legacy_80pct" && (
                                <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[9px] font-bold bg-slate-100 text-slate-600 border border-slate-200">
                                  LEGACY
                                </span>
                              )}
                              {e.gig_breakdown?.density_bonus > 0 && (
                                <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[9px] font-bold bg-pink-50 text-pink-700 border border-pink-200" data-testid={`density-badge-${e.tracking_id}`}>
                                  <Flame className="h-2.5 w-2.5" /> +₹{e.gig_breakdown.density_bonus}
                                </span>
                              )}
                            </div>
                          </TableCell>
                          <TableCell className="py-3">
                            <span className="text-sm text-slate-500 flex items-center gap-0.5">
                              <IndianRupee className="h-3 w-3" />{e.order_total || "—"}
                            </span>
                          </TableCell>
                          <TableCell className="py-3">
                            <span className="text-sm font-bold text-green-600 flex items-center gap-0.5">
                              <IndianRupee className="h-3.5 w-3.5" />{e.amount}
                            </span>
                          </TableCell>
                          <TableCell className="py-3">
                            <span className="text-xs text-slate-500">{new Date(e.created_at).toLocaleDateString()}</span>
                          </TableCell>
                        </TableRow>
                        {expanded && hasBreakdown && (
                          <TableRow className="bg-slate-50/40 border-slate-50" data-testid={`earning-breakdown-${e.tracking_id}`}>
                            <TableCell colSpan={5} className="py-4 px-6">
                              <div className="flex items-start gap-3">
                                <Sparkles className="h-4 w-4 text-emerald-500 mt-0.5 shrink-0" />
                                <div className="flex-1">
                                  <p className="text-[11px] font-bold uppercase tracking-wider text-slate-500 mb-2">Earnings Breakdown</p>
                                  <div className="flex items-center gap-1.5 flex-wrap">
                                    {chips.map((c, idx) => {
                                      const Icon = c.icon;
                                      return (
                                        <span key={c.label} className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-lg border text-[11px] font-semibold ${c.color}`}>
                                          <Icon className="h-3 w-3" />
                                          {c.label}
                                          <span className="font-bold ml-0.5">₹{c.value}</span>
                                          {idx < chips.length - 1 && <span className="text-slate-300 ml-0.5">+</span>}
                                        </span>
                                      );
                                    })}
                                    <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg border border-green-200 bg-green-50 text-green-700 text-[11px] font-bold">
                                      = ₹{e.amount}
                                    </span>
                                  </div>
                                  {e.gig_breakdown?.density_meta?.applied && (
                                    <p className="text-[10px] text-pink-600 mt-2 flex items-center gap-1">
                                      <Flame className="h-2.5 w-2.5" />
                                      Density bonus earned — {e.gig_breakdown.density_meta.count_in_window} pickups in the last {e.gig_breakdown.density_meta.window_minutes} min (threshold {e.gig_breakdown.density_meta.threshold}).
                                    </p>
                                  )}
                                </div>
                              </div>
                            </TableCell>
                          </TableRow>
                        )}
                      </Fragment>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          )}
        </div>
      )}

      {/* Payout History */}
      {tab === "payouts" && (
        <div className="bg-white rounded-2xl shadow-sm border border-slate-100 animate-fadeInUp" data-testid="payout-history">
          <div className="p-5 border-b border-slate-100">
            <h2 className="font-['Outfit'] text-lg font-semibold text-slate-900">Payout History</h2>
          </div>
          {payouts.length === 0 ? (
            <div className="p-12 text-center">
              <ArrowUpFromLine className="h-10 w-10 text-slate-300 mx-auto mb-3" />
              <p className="text-base font-semibold text-slate-700">No payouts yet</p>
              <p className="text-sm text-slate-400 mt-1">Request a payout when you have available balance</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="border-slate-100 bg-slate-50/60">
                    <TableHead className="text-[10px] font-bold uppercase tracking-widest text-slate-400 pl-6 py-3">Payout ID</TableHead>
                    <TableHead className="text-[10px] font-bold uppercase tracking-widest text-slate-400 py-3">Amount</TableHead>
                    <TableHead className="text-[10px] font-bold uppercase tracking-widest text-slate-400 py-3">Method</TableHead>
                    <TableHead className="text-[10px] font-bold uppercase tracking-widest text-slate-400 py-3">Details</TableHead>
                    <TableHead className="text-[10px] font-bold uppercase tracking-widest text-slate-400 py-3">Status</TableHead>
                    <TableHead className="text-[10px] font-bold uppercase tracking-widest text-slate-400 py-3">Requested</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {payouts.map((p) => (
                    <TableRow key={p.payout_id} className="border-slate-50 row-hover">
                      <TableCell className="pl-6 py-3">
                        <span className="text-sm font-mono font-semibold text-slate-900">{p.payout_id}</span>
                      </TableCell>
                      <TableCell className="py-3">
                        <span className="text-sm font-bold text-slate-900 flex items-center gap-0.5">
                          <IndianRupee className="h-3.5 w-3.5" />{p.amount}
                        </span>
                      </TableCell>
                      <TableCell className="py-3">
                        {methodBadge(p.withdrawal_method || "upi")}
                      </TableCell>
                      <TableCell className="py-3">
                        <span className="text-xs text-slate-500">
                          {p.withdrawal_method === "bank_transfer"
                            ? `${p.withdrawal_details?.bank_name || ""} ****${(p.withdrawal_details?.bank_account || "").slice(-4)}`
                            : p.withdrawal_details?.upi_id || "—"
                          }
                        </span>
                      </TableCell>
                      <TableCell className="py-3">{statusBadge(p.status)}</TableCell>
                      <TableCell className="py-3">
                        <span className="text-xs text-slate-500">{new Date(p.requested_at).toLocaleString()}</span>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
